import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Card } from './ui/card';
import { Upload, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Using relative URLs
const API = '/api';

const FileUploader = ({ 
  auditId, 
  controlId,
  evidenceIndex,
  onUploadComplete,
  onUploadStart,
  buttonVariant = "outline",
  buttonSize = "sm",
  buttonText = "Upload",
  buttonClassName = "",
  buttonIcon = null
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const errors = [];
    const maxSize = 100 * 1024 * 1024; // 100MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/jpg',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo'
    ];

    if (file.size > maxSize) {
      errors.push(`File size exceeds 100MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
    }

    if (!allowedTypes.includes(file.type)) {
      errors.push(`File type ${file.type} not supported`);
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      setErrorMessage(validation.errors.join(', '));
      setUploadStatus('error');
      toast.error(validation.errors[0]);
      setTimeout(() => setUploadStatus('idle'), 5000);
      return;
    }

    setSelectedFile(file);
    handleUpload(file);
  };

  const handleUpload = async (file) => {
    if (!file) return;

    setIsUploading(true);
    setUploadStatus('uploading');
    setUploadProgress(0);
    
    if (onUploadStart) {
      onUploadStart();
    }

    try {
      const token = localStorage.getItem('token');
      
      console.log('🔍 [FILE UPLOAD DEBUG] Starting server-side upload');
      console.log('📁 File Info:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('audit_id', auditId);
      if (controlId) {
        formData.append('control_id', controlId);
      }
      if (evidenceIndex !== undefined && evidenceIndex !== null) {
        formData.append('evidence_index', evidenceIndex.toString());
      }
      
      console.log('📤 [SERVER UPLOAD] Uploading file to backend...', { controlId, evidenceIndex });
      
      // Upload file to backend (which will upload to S3)
      const uploadResponse = await axios.post(
        `${API}/files/upload-direct`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentComplete = (progressEvent.loaded / progressEvent.total) * 100;
              setUploadProgress(Math.round(percentComplete));
            }
          }
        }
      );

      console.log('✅ [SERVER UPLOAD] File uploaded successfully:', uploadResponse.data);
      
      setUploadStatus('success');
      toast.success(`File "${file.name}" uploaded successfully`);
      
      if (onUploadComplete) {
        onUploadComplete({
          file_id: uploadResponse.data.file_id,
          filename: file.name,
          file_size: file.size,
          mime_type: file.type,
          control_id: controlId,
          evidence_index: evidenceIndex
        });
      }
      
      // Reset form
      setTimeout(() => {
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadStatus('idle');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 2000);
    } catch (error) {
      console.error('❌ [UPLOAD ERROR] Upload failed:', error);
      console.error('Error details:', {
        message: error.message,
        response_status: error.response?.status,
        response_data: error.response?.data,
        config_url: error.config?.url,
        config_headers: error.config?.headers
      });
      
      const errorMsg = error.response?.data?.detail || error.message || 'Upload failed';
      
      setErrorMessage(String(errorMsg));
      setUploadStatus('error');
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (uploadStatus === 'uploading') {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-32">
          <Progress value={uploadProgress} className="h-2" />
        </div>
        <span className="text-xs text-slate-600">{uploadProgress}%</span>
      </div>
    );
  }

  if (uploadStatus === 'success') {
    return (
      <div className="flex items-center text-green-600">
        <CheckCircle className="w-4 h-4 mr-1" />
        <span className="text-xs">Uploaded</span>
      </div>
    );
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.mp4,.mov,.avi"
      />
      <Button 
        variant={buttonVariant} 
        size={buttonSize}
        onClick={handleButtonClick}
        disabled={isUploading}
        className={buttonClassName || "text-xs"}
      >
        {isUploading ? (
          <>
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            {buttonIcon || <Upload className="w-3 h-3 mr-1" />}
            {buttonText}
          </>
        )}
      </Button>
    </>
  );
};

export default FileUploader;
